<template>
  <div class="relative space-y-2">
    <div class="relative">
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        :placeholder="selectedEntity ? '' : placeholderText"
        class="w-full px-2.5 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-gray-400"
        @input="onSearchInput"
        @focus="onSearchFocus"
        @blur="onSearchBlur"
        @keydown.enter.prevent="selectFirstMatch"
        @keydown.escape="closeDropdown"
        @keydown.arrow-down.prevent="navigateDown"
        @keydown.arrow-up.prevent="navigateUp" />
      <span
        v-if="loading"
        class="absolute right-2 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400 animate-spin">
        progress_activity
      </span>
      <button
        v-else-if="selectedEntity"
        class="absolute right-2 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400 hover:text-gray-600"
        :title="tt('Clear selection', 'Počisti izbiro')"
        @mousedown.prevent="clearSelection">
        close
      </button>
      <span v-else class="absolute right-2 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400">
        search
      </span>
    </div>

    <div
      v-if="selectedEntity && !showDropdown"
      class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700">
      <span class="material-symbols-outlined text-sm text-emerald-500">lan</span>
      <div class="flex-1 min-w-0">
        <div class="text-xs font-medium text-gray-900 dark:text-white truncate">
          {{ selectedEntity.name || selectedEntity.objectId || selectedEntity.entityKey }}
        </div>
        <div class="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
          {{ selectedEntity.entityKey }}
        </div>
        <div class="text-xs text-gray-400 dark:text-gray-500 truncate">
          {{
            selectedEntity.device?.friendlyName ||
            selectedEntity.device?.nodeName ||
            tt('Unknown node', 'Neznano vozlišče')
          }}
        </div>
      </div>
      <div class="flex items-center gap-1.5 flex-shrink-0">
        <span
          class="px-1.5 py-0.5 text-xs rounded bg-emerald-100 dark:bg-emerald-800/40 text-emerald-700 dark:text-emerald-300">
          {{ selectedEntity.entityType }}
        </span>
        <span
          v-if="selectedEntity.isWritable"
          class="px-1.5 py-0.5 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
          {{ tt('Writable', 'Za pisanje') }}
        </span>
      </div>
    </div>

    <div v-if="selectedEntity && referenceType === 'status'" class="space-y-1">
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">{{
        tt('Source field', 'Polje vira')
      }}</label>
      <div class="flex gap-2">
        <select
          v-model="selectedField"
          class="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          @change="emitSelection">
          <option v-for="option in fieldOptions" :key="option.value || '__auto'" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <input
          v-if="showCustomField"
          v-model="selectedField"
          type="text"
          placeholder="customField"
          class="w-40 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
          @change="emitSelection" />
        <button
          class="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg"
          :title="tt('Type a custom telemetry field', 'Vnesite polje telemetrije po meri')"
          @click="showCustomField = !showCustomField">
          <span class="material-symbols-outlined text-sm">edit</span>
        </button>
      </div>
      <p class="text-xs text-gray-400 dark:text-gray-500">
        {{
          tt(
            "Auto uses ESPHome's default state/value extraction. Pick a field for composite entities like climate, cover, fan, or update.",
            'Samodejno uporabi privzeto pridobivanje stanja/vrednosti ESPHome. Izberite polje za sestavljene entitete, kot so climate, cover, fan ali update.',
          )
        }}
      </p>
    </div>

    <div
      v-if="showDropdown"
      class="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl">
      <div
        v-if="referenceType === 'control'"
        class="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
        {{
          tt(
            'Showing writable entities for control links.',
            'Prikaz entitet, ki jih je mogoče pisati, pri krmilnih povezavah.',
          )
        }}
      </div>

      <ul class="py-1">
        <li
          v-for="(entity, index) in filteredEntities"
          :key="entity.id"
          :class="[
            'px-3 py-2 cursor-pointer transition-colors',
            highlightedIndex === index
              ? 'bg-emerald-50 dark:bg-emerald-900/30'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700',
          ]"
          @mousedown.prevent="selectEntity(entity)"
          @mouseenter="highlightedIndex = index">
          <div class="flex items-start justify-between gap-2">
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900 dark:text-white truncate">
                {{ entity.name || entity.objectId || entity.entityKey }}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400 font-mono truncate mt-0.5">
                {{ entity.entityKey }}
              </div>
              <div class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                {{ entity.device?.friendlyName || entity.device?.nodeName || tt('Unknown node', 'Neznano vozlišče') }}
              </div>
            </div>
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <span
                class="px-1.5 py-0.5 text-xs rounded bg-emerald-100 dark:bg-emerald-800/40 text-emerald-700 dark:text-emerald-300">
                {{ entity.entityType }}
              </span>
              <span
                v-if="entity.isWritable"
                class="px-1.5 py-0.5 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                W
              </span>
              <span
                v-if="entity.currentValue !== null && entity.currentValue !== undefined"
                class="px-1.5 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 max-w-28 truncate">
                {{ formatValue(entity.currentValue, entity.unit) }}
              </span>
            </div>
          </div>
        </li>
      </ul>

      <div
        v-if="filteredEntities.length === 0 && !loading"
        class="px-3 py-3 text-xs text-center text-gray-500 dark:text-gray-400">
        <span v-if="error" class="text-red-500">{{ error }}</span>
        <span v-else-if="searchQuery"
          >{{ tt('No ESPHome entities matching', 'Ni entitet ESPHome, ki bi se ujemale z') }} "{{ searchQuery }}"</span
        >
        <span v-else>{{
          tt('No ESPHome entities found for this integration', 'Za to integracijo ni najdenih entitet ESPHome')
        }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';

export interface ESPHomeEntityRef {
  esphomeEntityId: string;
  entityKey?: string;
  entityType?: string;
  objectId?: string;
  name?: string;
  deviceId?: string;
  deviceName?: string;
  isWritable?: boolean;
  available?: boolean;
  valueType?: string;
  unit?: string;
  field?: string;
}

interface ESPHomeEntityRecord {
  id: string;
  deviceId: string;
  entityKey: string;
  entityType: string;
  objectId?: string | null;
  name?: string | null;
  unit?: string | null;
  valueType: string;
  currentValue?: unknown;
  isWritable: boolean;
  available: boolean;
  device?: {
    id: string;
    nodeName: string;
    friendlyName?: string | null;
  };
}

interface Props {
  modelValue?: ESPHomeEntityRef;
  integrationId: string;
  referenceType: 'status' | 'control';
}

const FIELD_PRESETS: Record<string, Array<{ value: string; label: [string, string] }>> = {
  climate: [
    { value: 'state', label: ['State', 'Stanje'] },
    { value: 'targetTemperature', label: ['Target temperature', 'Ciljna temperatura'] },
    { value: 'currentTemperature', label: ['Current temperature', 'Trenutna temperatura'] },
    { value: 'mode', label: ['Mode', 'Način'] },
  ],
  cover: [
    { value: 'state', label: ['State', 'Stanje'] },
    { value: 'position', label: ['Position', 'Položaj'] },
  ],
  fan: [
    { value: 'state', label: ['State', 'Stanje'] },
    { value: 'speedLevel', label: ['Speed level', 'Stopnja hitrosti'] },
  ],
  light: [
    { value: 'state', label: ['State', 'Stanje'] },
    { value: 'brightness', label: ['Brightness', 'Svetlost'] },
  ],
  update: [
    { value: 'latestVersion', label: ['Latest version', 'Najnovejša različica'] },
    { value: 'currentVersion', label: ['Current version', 'Trenutna različica'] },
    { value: 'progress', label: ['Progress', 'Napredek'] },
  ],
  event: [{ value: 'eventType', label: ['Event type', 'Vrsta dogodka'] }],
  datetime: [{ value: 'epochSeconds', label: ['Epoch seconds', 'Sekunde epohe'] }],
  date: [
    { value: 'year', label: ['Year', 'Leto'] },
    { value: 'month', label: ['Month', 'Mesec'] },
    { value: 'day', label: ['Day', 'Dan'] },
  ],
  time: [
    { value: 'hour', label: ['Hour', 'Ura'] },
    { value: 'minute', label: ['Minute', 'Minuta'] },
    { value: 'second', label: ['Second', 'Sekunda'] },
  ],
};

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: ESPHomeEntityRef | undefined];
}>();

/** The caches this picker shares across instances, hung off globalThis. */
interface ESPHomeGlobalCache {
  __ottoESPHomeEntityCache?: Map<string, ESPHomeEntityRecord[]>;
  __ottoESPHomeEntityRequestCache?: Map<string, Promise<ESPHomeEntityRecord[]>>;
}

const globalCache = globalThis as ESPHomeGlobalCache;

const entityCache = (globalCache.__ottoESPHomeEntityCache ??= new Map<string, ESPHomeEntityRecord[]>()) as Map<
  string,
  ESPHomeEntityRecord[]
>;
const entityRequestCache = (globalCache.__ottoESPHomeEntityRequestCache ??= new Map<
  string,
  Promise<ESPHomeEntityRecord[]>
>()) as Map<string, Promise<ESPHomeEntityRecord[]>>;

const searchInput = ref<HTMLInputElement>();
const searchQuery = ref('');
const showDropdown = ref(false);
const loading = ref(false);
const error = ref<string | null>(null);
const entities = ref<ESPHomeEntityRecord[]>([]);
const selectedEntity = ref<ESPHomeEntityRecord | undefined>(undefined);
const highlightedIndex = ref(0);
const selectedField = ref('');
const showCustomField = ref(false);

const placeholderText = computed(() =>
  props.referenceType === 'control'
    ? tt(
        'Search writable ESPHome entity (e.g. switch.relay_1)',
        'Iskanje entitete ESPHome, ki jo je mogoče pisati (npr. switch.relay_1)',
      )
    : tt(
        'Search ESPHome entity (e.g. sensor.power, switch.relay_1)',
        'Iskanje entitete ESPHome (npr. sensor.power, switch.relay_1)',
      ),
);

const filteredEntities = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  let result = entities.value.filter((entity) => {
    if (props.referenceType === 'control' && !entity.isWritable && entity.id !== selectedEntity.value?.id) {
      return false;
    }
    if (!query) {
      return true;
    }
    return [
      entity.entityKey,
      entity.name,
      entity.objectId,
      entity.entityType,
      entity.device?.friendlyName,
      entity.device?.nodeName,
    ].some((value) => (value ?? '').toLowerCase().includes(query));
  });

  result = result.sort((a, b) =>
    (a.name || a.objectId || a.entityKey).localeCompare(b.name || b.objectId || b.entityKey),
  );

  return result.slice(0, 60);
});

const fieldOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [
    { value: '', label: tt('Auto (state/value)', 'Samodejno (stanje/vrednost)') },
  ];
  const seen = new Set<string>(['']);
  const addOption = (value?: string | null, label?: string | [string, string]) => {
    if (!value || seen.has(value)) {
      return;
    }
    seen.add(value);
    options.push({ value, label: Array.isArray(label) ? tt(...label) : label || value });
  };

  const entityType = selectedEntity.value?.entityType || props.modelValue?.entityType || '';
  for (const option of FIELD_PRESETS[entityType] || []) {
    addOption(option.value, option.label);
  }

  const currentValue = selectedEntity.value?.currentValue;
  if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)) {
    for (const key of Object.keys(currentValue)) {
      addOption(key, key);
    }
  }

  if (props.modelValue?.field) {
    addOption(props.modelValue.field, `${props.modelValue.field} ${tt('(custom)', '(po meri)')}`);
  }

  return options;
});

function formatValue(value: unknown, unit?: string | null): string {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  const rendered = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return unit ? `${rendered} ${unit}` : rendered;
}

function syncFieldEditorState() {
  const knownFields = new Set(fieldOptions.value.map((option) => option.value));
  showCustomField.value = Boolean(selectedField.value && !knownFields.has(selectedField.value));
}

async function fetchEntities(integrationId: string): Promise<ESPHomeEntityRecord[]> {
  if (entityCache.has(integrationId)) {
    return entityCache.get(integrationId) || [];
  }

  if (entityRequestCache.has(integrationId)) {
    return entityRequestCache.get(integrationId) || [];
  }

  const request = (async () => {
    const data = await apiFetch(`/integrations/${integrationId}/esphome/entities`);
    const list = Array.isArray(data) ? data : [];
    entityCache.set(integrationId, list);
    entityRequestCache.delete(integrationId);
    return list;
  })();

  entityRequestCache.set(integrationId, request);
  return request;
}

async function loadEntities() {
  if (!props.integrationId) return;
  if (entities.value.length > 0) return;

  loading.value = true;
  error.value = null;
  try {
    entities.value = await fetchEntities(props.integrationId);
    syncSelectionFromModel();
  } catch (__caught: unknown) {
    const err = __caught as Error;
    error.value = err.message || tt('Failed to load ESPHome entities', 'Entitet ESPHome ni bilo mogoče naložiti');
    console.error('[ESPHomeReferenceInput] Load error:', err);
  } finally {
    loading.value = false;
  }
}

function buildRef(entity: ESPHomeEntityRecord): ESPHomeEntityRef {
  return {
    esphomeEntityId: entity.id,
    entityKey: entity.entityKey,
    entityType: entity.entityType,
    objectId: entity.objectId ?? undefined,
    name: entity.name ?? undefined,
    deviceId: entity.deviceId,
    deviceName: entity.device?.friendlyName || entity.device?.nodeName,
    isWritable: entity.isWritable,
    available: entity.available,
    valueType: entity.valueType,
    unit: entity.unit ?? undefined,
    field: selectedField.value || undefined,
  };
}

function syncSelectionFromModel() {
  const refValue = props.modelValue;
  if (!refValue?.esphomeEntityId) {
    selectedEntity.value = undefined;
    selectedField.value = '';
    showCustomField.value = false;
    if (!showDropdown.value) {
      searchQuery.value = '';
    }
    return;
  }

  const match = entities.value.find((entity) => entity.id === refValue.esphomeEntityId);
  if (!match) {
    return;
  }

  selectedEntity.value = match;
  selectedField.value = refValue.field || '';
  syncFieldEditorState();
  if (!showDropdown.value) {
    searchQuery.value = '';
  }

  if (
    refValue.entityKey !== match.entityKey ||
    refValue.entityType !== match.entityType ||
    refValue.name !== (match.name ?? undefined)
  ) {
    emit('update:modelValue', buildRef(match));
  }
}

function emitSelection() {
  if (!selectedEntity.value) {
    return;
  }
  syncFieldEditorState();
  emit('update:modelValue', buildRef(selectedEntity.value));
}

function onSearchInput() {
  showDropdown.value = true;
  highlightedIndex.value = 0;
}

async function onSearchFocus() {
  await loadEntities();
  showDropdown.value = true;
}

function onSearchBlur() {
  setTimeout(() => {
    showDropdown.value = false;
    if (selectedEntity.value) {
      searchQuery.value = '';
    }
  }, 200);
}

function closeDropdown() {
  showDropdown.value = false;
  searchInput.value?.blur();
}

function navigateDown() {
  if (highlightedIndex.value < filteredEntities.value.length - 1) {
    highlightedIndex.value += 1;
  }
}

function navigateUp() {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value -= 1;
  }
}

function selectFirstMatch() {
  if (filteredEntities.value.length > 0) {
    selectEntity(filteredEntities.value[highlightedIndex.value]);
  }
}

function selectEntity(entity: ESPHomeEntityRecord) {
  selectedEntity.value = entity;
  searchQuery.value = '';
  showDropdown.value = false;
  syncFieldEditorState();
  emitSelection();
}

function clearSelection() {
  selectedEntity.value = undefined;
  selectedField.value = '';
  showCustomField.value = false;
  searchQuery.value = '';
  highlightedIndex.value = 0;
  emit('update:modelValue', undefined);
}

watch(
  () => props.integrationId,
  () => {
    entities.value = [];
    selectedEntity.value = undefined;
    selectedField.value = '';
    showCustomField.value = false;
    error.value = null;
  },
);

watch(
  () => props.modelValue,
  async (newValue) => {
    if (!newValue?.esphomeEntityId) {
      selectedEntity.value = undefined;
      if (!showDropdown.value) {
        searchQuery.value = '';
      }
      return;
    }

    if (entities.value.length === 0) {
      await loadEntities();
      return;
    }

    syncSelectionFromModel();
  },
  { immediate: true, deep: true },
);
</script>
